import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ReactNode, useMemo, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { intersection } from "underscore";
import classNames from "classnames";

import {
  Field,
  LayoutAndSections,
  Module,
} from "../../../../types/TenantInterfaces";
import { GetDefaultLayout } from "../../../Drawers/Records/ModuleLayoutSelector";
import useUpdateRecord from "../../../../api/api-hooks/records/useUpdateRecord";
import UpdateRecordDrawer from "../../../Drawers/Records/UpdateRecordDrawer";
import { ReusableViewIDs, View } from "../../../../types/PageInterfaces";
import useRecordMetadata from "../../../../hooks/useRecordMetadata";
import useDefaultPrefill from "../SharedToolbar/useDefaultPrefill";
import SkeletonLoader from "../../../../elements/SkeletonLoader";
import { shrinkCardsAtom } from "../../../../atoms/GlobalAtoms";
import AppEmptyState from "../../../../elements/AppEmptyState";
import useRecordImage from "../../../../hooks/useRecordImage";
import { Prefill } from "../../../../types/GlobalInterfaces";
import useFieldMap from "../../../../hooks/useFieldMap";
import CardRecord from "../PageViewCards/CardRecord";
import useHover from "../../../../hooks/useHover";
import Droppable from "../../../Dnd/Droppable";

export interface BoardProps {
  records: any[];
  recordsLoading: boolean;
  currentModule: Module;
  selectedRecords: { [key: number]: boolean };
  setSelectedRecords: (newObj: object) => void;
  onOpenRecordName: any;
  query: any;
  groupBy: string;
  groups: any[];
  loading: boolean;
  groupByFieldSchema?: Field;
  defaultRecordLayout?: string;
  toggleCreateRecordsDrawer: () => void;
  setSelectedPrefill: React.Dispatch<React.SetStateAction<Prefill | null>>;
  view: View;
  reusableViews?: ReusableViewIDs;
}

export default function Board({
  records,
  recordsLoading,
  currentModule,
  selectedRecords,
  setSelectedRecords,
  query,
  loading,
  groupBy,
  groups,
  groupByFieldSchema,
  defaultRecordLayout,
  toggleCreateRecordsDrawer,
  setSelectedPrefill,
  view,
  onOpenRecordName,
  reusableViews,
}: BoardProps) {
  const groupByFieldType = groupByFieldSchema?.FieldType;
  const defaultPrefill = useDefaultPrefill({ view });
  const shrink = useAtomValue(shrinkCardsAtom);
  const [isDragging, setIsDragging] = useState(false);
  const { updateRecord } = useUpdateRecord(true, "");
  const [layout, _] = useState<null | LayoutAndSections>(
    GetDefaultLayout(currentModule, defaultRecordLayout) || null
  );
  const pointerSensor = useSensor(PointerSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(pointerSensor, touchSensor, keyboardSensor);

  const [updateRecordID, setUpdateRecordID] = useState<string | null>(null);
  // @ts-ignore
  const { imageField, imageFieldPresentation } = useRecordImage({
    module: currentModule,
  });
  // @ts-ignore
  const { metadataField, metadataFieldType } = useRecordMetadata({
    module: currentModule,
  });

  const selectedIds = Object.keys(selectedRecords);

  const fields = query?.Fields;

  // Fields section for the Card record
  const fieldsSection = useFieldsSection(currentModule?.APIName, fields);

  if (!loading && (!groups || !groups.length)) {
    return (
      <AppEmptyState
        title="No Records Found"
        description="Currently, there are no records that match the selected grouping field. This could be because none of the records have this specific field set. Please consider adjusting your grouping criteria or adding relevant records to see the desired results."
        icon="mdi-clipboard-search"
      />
    );
  }

  return (
    <>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div className="d-flex mx--3 pb-4" style={{ overflowX: "auto" }}>
          {/* Loading state when no groups are present yet! */}
          {!Boolean(groups.length) &&
            loading &&
            [1, 2, 3, 4].map((num) => {
              return (
                <div
                  key={num}
                  className={"mx-1 p-2 rounded bg-superlight"}
                  style={{
                    width: shrink ? 320 : 380,
                    minHeight: "calc(100vh - 225px)",
                    height: "100%",
                  }}
                >
                  <div className="mx-2">
                    <SkeletonLoader height={32} />
                  </div>
                  <div className="d-flex flex-column gap-2 py-2 px-1">
                    <Loader />
                    <Loader />
                    <Loader />
                  </div>
                </div>
              );
            })}
          {groups.map((option: any) => {
            const groupByKey = groupBy.includes(".")
              ? groupBy.split(".")[0]
              : groupBy;
            let recordsGroup;

            if (groupByFieldType === "SingleReference") {
              recordsGroup = records?.find(
                (r) => r?.GroupBy?.[groupBy]?.id === option?.Value?.id
              );
            } else {
              recordsGroup = records?.find(
                (r) => r?.GroupBy?.[groupByKey] === option?.Value
              );
            }

            const data = recordsGroup?.GroupBy?.records ?? [];

            const count = data.length ?? 0;

            const recordIds = data.map((r: any) => r._id);
            const intersect = intersection(selectedIds, recordIds);
            const isSelected = intersect.length === recordIds.length;
            const optionValue = option?.Value
              ? typeof option?.Value === "object"
                ? JSON.stringify(option?.Value)
                : option?.Value
              : "";

            return (
              <Droppable
                key={optionValue || "EMPTY"}
                id={optionValue || "EMPTY"}
                childComponent={(isOver) => {
                  return (
                    <BoardGroup
                      isDragging={isDragging}
                      isOver={isOver}
                      title={option.Name}
                      recordsLoading={recordsLoading}
                      count={count}
                      isSelected={isSelected}
                      selectableRows={!currentModule?.Dynamic}
                      disabledCreateRecordButton={!!currentModule?.Dynamic}
                      handleSelectAll={() => handleSelectAll(data, isSelected)}
                      onCreate={() => {
                        const isMulti = groupByFieldType?.startsWith("Multi");

                        const groupByPrefill = {
                          Field: groupBy,
                          Value: isMulti ? [option.Value] : option.Value,
                        };

                        // Merge prefill with the view's default prefill.
                        const prefill = defaultPrefill
                          ? {
                              ...defaultPrefill,
                              PrefillPairs: defaultPrefill.PrefillPairs.map(
                                (p: any) =>
                                  p.Field === groupByPrefill.Field
                                    ? groupByPrefill
                                    : p
                              ),
                            }
                          : {
                              Name: "Board Prefill",
                              ModuleAPIName: currentModule?.APIName,
                              id: optionValue,
                              PrefillPairs: [groupByPrefill],
                              Icon: "mdi mdi-plus-box",
                            };

                        // If group by didn't exist in default prefill, add it
                        if (
                          defaultPrefill &&
                          !prefill?.PrefillPairs?.find?.(
                            (p: any) => p?.Field === groupByPrefill.Field
                          )
                        ) {
                          prefill.PrefillPairs.push(groupByPrefill);
                        }

                        setSelectedPrefill(prefill);
                        toggleCreateRecordsDrawer();
                      }}
                    >
                      <div className="d-flex flex-column gap-1 py-2 px-1">
                        {loading && (
                          <>
                            <Loader />
                            <Loader />
                            <Loader />
                          </>
                        )}
                        {!loading &&
                          data.map((r: any) => {
                            return (
                              <CardRecord
                                key={r?._id}
                                record={r}
                                imageField={imageField}
                                imageFieldPresentation={imageFieldPresentation}
                                currentModule={currentModule}
                                setOpenRecordData={onOpenRecordName}
                                metadataField={metadataField}
                                metadataFieldType={metadataFieldType}
                                fieldsSection={fieldsSection}
                                draggable={
                                  !groupByFieldSchema?.ReadOnly ||
                                  !currentModule?.Dynamic
                                }
                                isSelected={!!selectedRecords[r._id]}
                                onSelect={() => handleSelect(r)}
                                setUpdateRecordID={setUpdateRecordID}
                                startDroppableID={optionValue || "EMPTY"}
                                shrink={shrink}
                                viewID={view?.id}
                                reusableViews={reusableViews}
                              />
                            );
                          })}
                      </div>
                    </BoardGroup>
                  );
                }}
              />
            );
          })}
        </div>
      </DndContext>
      <UpdateRecordDrawer
        open={!!updateRecordID}
        onClose={() => setUpdateRecordID(null)}
        recordID={updateRecordID!}
        moduleAPIName={currentModule?.APIName}
        layout={layout}
      />
    </>
  );

  function handleSelectAll(records: any[], isSelected: boolean) {
    setSelectedRecords((prev: any) => {
      if (!Array.isArray(records)) return prev;

      const newRecords = { ...prev };

      for (const r of records) {
        if (isSelected) delete newRecords[r._id];
        else newRecords[r._id] = true;
      }

      return newRecords;
    });
  }

  // Handles when a Card is selected AND unselected.
  function handleSelect(r: any) {
    setSelectedRecords((prev: any) => {
      const newRecords = { ...prev };

      if (newRecords[r._id]) delete newRecords[r._id];
      else newRecords[r._id] = true;

      return newRecords;
    });
  }

  function handleDragStart() {
    setIsDragging(true);
  }

  function getIdValue(id: any) {
    if (id === "EMPTY") return null;

    if (groupByFieldType === "SingleReference") {
      try {
        const res = JSON.parse(id);
        return res;
      } catch {
        return id;
      }
    }

    if (groupByFieldType === "MultiReference") {
      try {
        const res = JSON.parse(id);

        return Array.isArray(res) ? res : [res];
      } catch {
        return null;
      }
    }

    return id;
  }

  function handleDragEnd(event: DragEndEvent) {
    setIsDragging(false);
    const recordID = event.active.id;
    const droppableID = event.over?.id;
    const activeData = event.active.data.current as any;
    const newRecord = structuredClone(activeData.record);
    const startDroppableID = activeData.startDroppableID;

    if (!recordID || !droppableID || !newRecord) return;

    if (startDroppableID === droppableID) return;

    newRecord.id = newRecord._id;

    updateRecord({
      field: groupByFieldSchema,
      recordID: recordID as string,
      payload: { [groupBy]: getIdValue(droppableID) },
      moduleAPIName: currentModule?.APIName,
      originalRecord: newRecord,
      _UserActivity: undefined,
    });
  }
}

function Loader() {
  const shrink = useAtomValue(shrinkCardsAtom);

  return (
    <SkeletonLoader
      width={shrink ? "295px" : "355px"}
      height={200}
      css="my-0"
    />
  );
}

function BoardGroup({
  isDragging,
  isOver,
  title,
  recordsLoading,
  count,
  children,
  handleSelectAll,
  isSelected,
  onCreate,
  selectableRows,
  disabledCreateRecordButton,
}: {
  isDragging: boolean;
  isOver?: boolean;
  title: string;
  recordsLoading: boolean;
  count: number;
  children: ReactNode;
  handleSelectAll?: () => void;
  isSelected: boolean;
  onCreate: () => void;
  selectableRows: boolean;
  disabledCreateRecordButton: boolean;
}) {
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  const [shrink, setShrinkCards] = useAtom(shrinkCardsAtom);

  return (
    <div
      className={classNames("mx-1 p-2 rounded", {
        "bg-superlight": !isOver,
        "bg-primary-selected": isOver,
      })}
      style={{
        width: shrink ? 320 : 380,
        minHeight: "calc(100vh - 275px)",
        height: "100%",
      }}
      ref={hoverRef}
    >
      <div
        className={classNames(
          "d-flex font-weight-bold justify-content-between align-items-center mx-2 text-sm mb--1",
          { "text-dark": isOver }
        )}
        style={{ height: 30 }}
      >
        {title}
        {!recordsLoading && (
          <div className="d-flex align-items-center">
            {isHovered && !isDragging ? (
              <>
                {count > 0 && selectableRows && (
                  <div
                    onClick={handleSelectAll}
                    className="d-flex align-items-center cursor-pointer px-2"
                  >
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={isSelected}
                      readOnly
                    />
                  </div>
                )}
                <i
                  className={`mdi mdi-${
                    !shrink ? "arrow-collapse" : "arrow-expand"
                  } text-lg mr-2 cursor-pointer text-dark--hover `}
                  onClick={() => setShrinkCards((prev) => !prev)}
                />
                {!disabledCreateRecordButton && (
                  <i
                    onClick={onCreate}
                    className="mdi mdi-plus bg-primary text-white cursor-pointer text-lg rounded-sm"
                    style={{
                      lineHeight: 0.75,
                      padding: "4px 2px",
                    }}
                  />
                )}
              </>
            ) : (
              <div
                className={classNames(
                  "py-1 px-2 d-flex align-items-center rounded",
                  {
                    "bg-dropdown-selected-dark": isOver,
                    "bg-dropdown-selected": !isOver,
                  }
                )}
                style={{ lineHeight: 1 }}
              >
                {count}
              </div>
            )}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// Fields section for the Card record
function useFieldsSection(moduleAPIName: string, fields: string[]) {
  const fieldMap: { [key: string]: any } = useFieldMap(moduleAPIName);

  const fieldsSection = useMemo(() => {
    if (!fieldMap || !Array.isArray(fields)) return { Fields: [], Title: "" };
    const isSmall = fields.length <= 5;

    return {
      Title: "",
      Type: "RecordSummarySection",
      Fields: fields.flatMap((f) => {
        if (["id", "_RecordName"].includes(f)) return [];

        return [
          {
            Field: fieldMap[f],
            ColSpan: isSmall ? 1 : 1,
          },
        ];
      }),
    };
  }, [fieldMap, fields]);

  return fieldsSection;
}
